import type { JSONContent } from "@tiptap/core";
import type { ReactNode } from "react";

// Wraps a text node's content in its marks, matching the DOM the editor
// produces (`<strong>`, `<em>`, `<s>`, `<u>`, `<mark class="highlight-class">`,
// `<a class="text-link">`). Applied inner-to-outer so nested marks compose.
export const renderTextWithMarks = (node: JSONContent): ReactNode => {
  const text = node.text ?? "";
  const marks = node.marks ?? [];

  return marks.reduceRight<ReactNode>((children, mark) => {
    switch (mark.type) {
      case "bold":
        return <strong>{children}</strong>;
      case "italic":
        return <em>{children}</em>;
      case "strike":
        return <s>{children}</s>;
      case "underline":
        return <u>{children}</u>;
      case "highlight":
        return <mark className="highlight-class">{children}</mark>;
      case "link": {
        // Block javascript: and data: schemes — the full editor guards against
        // these via CustomizedLink.parseHTML and sanitizeLinkMarks, but the
        // static path bypasses both. Defang rather than omit so the link is
        // still visually present.
        const href = String(mark.attrs?.href ?? "");
        const safeHref = /^(javascript|data):/i.test(href) ? "#" : href;
        const target = String(mark.attrs?.target ?? "_blank");
        return (
          <a
            href={safeHref}
            target={target}
            rel="noopener noreferrer"
            className="text-link"
          >
            {children}
          </a>
        );
      }
      default:
        return children;
    }
  }, text);
};
